import { useAuth0 } from '@auth0/auth0-react';
import { useState, useEffect, useMemo, useCallback } from 'react';

import { authConfig, authScopes } from '../auth/config';
import { grantsPermission, permissionNames as P } from '../auth/permissions.generated';

const AUTH0_AUDIENCE = authConfig.audience;

export interface UserPermissions {
  allows?: (permission: string) => boolean;
  canRemoveCollectionMemberships?: boolean;
  canAddCollectionMemberships?: boolean;
  canDeleteCollections?: boolean;
  canDeleteConcepts?: boolean;
  canUpdateConcepts?: boolean;
  canCreateConcepts?: boolean;
  canUpdateCampaigns?: boolean;
  canCreateDemands: boolean;
  canViewDemandBacklog: boolean;
  canManageDemands: boolean;
  canViewBoardGovernance: boolean;
  canViewProjectPortfolio: boolean;
  canViewReports: boolean;
  canManageSettings: boolean;
  canGeneratePDFs: boolean;
  isAdmin: boolean;
  canAccessDemandManagement: boolean; // NEW: permission for Demand Management section
  canAccessDemandDashboard: boolean;  // NEW: permission for Demand Dashboard subnav
  canAccessCampaignManagement: boolean; // NEW: permission for Campaign Management section
  canAddCampaigns: boolean; // NEW: permission to add campaigns
  canDeleteCampaigns: boolean; // NEW: permission to delete campaigns
  canManageHubSpot: boolean; // NEW: permission for HubSpot Management section
  canAccessConceptManagement: boolean;
  canManageConcepts: boolean;
  canReadCollections: boolean;
  canCreateCollections: boolean;
  canUpdateCollections: boolean;
  canWriteCollectionMemberships: boolean;
  canReadProductSearch: boolean;
  canManageProductSearchSync: boolean;
}

// Helper function to decode JWT payload
function decodeJWT(token: string): Record<string, unknown> {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return {};
  }
}

// Global cache for permissions to avoid multiple API calls
let permissionsCache: {
  permissions: string[];
  roles: string[];
  userId: string | null;
  timestamp: number;
} | null = null;

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

export function usePermissions(): UserPermissions {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const userId = user?.sub;

  // Memoized permission calculations
  const calculatedPermissions = useMemo(() => {

    if (!isAuthenticated || !user || loading) {
        return {
        canUpdateCampaigns: false,
        canCreateConcepts: false,
        canUpdateConcepts: false,
        canDeleteConcepts: false,
        canDeleteCollections: false,
        canAddCollectionMemberships: false,
        canRemoveCollectionMemberships: false,
        allows: () => false,
        canCreateDemands: false,
        canViewDemandBacklog: false,
        canManageDemands: false,
        canViewBoardGovernance: false,
        canViewProjectPortfolio: false,
        canViewReports: false,
        canManageSettings: false,
        canGeneratePDFs: false,
        isAdmin: false,
        canAccessDemandManagement: false,
        canAccessDemandDashboard: false,
        canAccessCampaignManagement: false,
        canAddCampaigns: false,
        canDeleteCampaigns: false,
        canManageHubSpot: false,
        canAccessConceptManagement: false,
        canManageConcepts: false,
        canReadCollections: false,
        canCreateCollections: false,
        canUpdateCollections: false,
        canWriteCollectionMemberships: false,
        canReadProductSearch: false,
        canManageProductSearchSync: false,
      };
    }

    // Helper function to check if user has permission
    const hasPermission = (permission: string) => {
      const result = grantsPermission(permissions, permission);
        return result;
    };
    const hasRole = (role: string) => {
      const result = roles.includes(role);
        return result;
    };

    // Check for admin privileges
    const isAdmin = hasRole('admin') ||
                    hasRole('super-admin') ||
                    hasRole('Administrator') ||
                    hasPermission(P.AdministrationManage);


    // New permissions for Demand Management and Dashboard
    const canAccessDemandManagement = hasPermission(P.AccessDemandManagement) || isAdmin;
    const canAccessDemandDashboard = hasPermission(P.AccessDemandDashboard) || isAdmin;
    const canAccessCampaignManagement = Object.values(P).filter(p => p.startsWith("campaign:")).some(hasPermission) || isAdmin;
    const canAddCampaigns = hasPermission(P.CampaignRecordCreate) || isAdmin;
    const canDeleteCampaigns = hasPermission(P.CampaignRecordDelete) || isAdmin;
    const canManageHubSpot = Object.values(P).filter(p => p.startsWith("hubspot:")).some(hasPermission) || isAdmin; // NEW: manage HubSpot permission
    const canAccessConceptManagement = hasPermission(P.CampaignConceptRead) || grantsPermission(permissions, 'manage:concepts') || isAdmin;
    const canManageConcepts = [P.CampaignConceptCreate, P.CampaignConceptUpdate, P.CampaignConceptDelete].some(hasPermission) || isAdmin;
    const canReadProductSearch = hasPermission(P.ReadProductSearch) || isAdmin;
    const canManageProductSearchSync = hasPermission(P.CatalogSearchSyncRead) || isAdmin;


    const result = {
      allows: (permission: string) => hasPermission(permission) || isAdmin || (permission.startsWith("product:") && hasRole("ProductManager")),
      // Map Auth0 API permissions to application permissions
      canCreateDemands: hasPermission(P.CreateDemands) ||
                       hasRole('DemandCreator') ||
                       isAdmin,

      canViewDemandBacklog: hasPermission(P.ReadDemands) ||
                           hasRole('DemandViewer') ||
                           isAdmin,

      canManageDemands: hasPermission(P.UpdateDemands) ||
                       hasPermission(P.DeleteDemands) ||
                       hasRole('DemandManager') ||
                       isAdmin,

      canViewBoardGovernance: hasPermission(P.ReadGovernance) ||
                             hasRole('GovernanceViewer') ||
                             isAdmin,

      canViewProjectPortfolio: hasPermission(P.ReadProjects) ||
                              hasRole('ProjectViewer') ||
                              isAdmin,

      canViewReports: hasPermission(P.ReadReports) ||
                     hasRole('ReportViewer') ||
                     isAdmin,

      canUpdateCampaigns: hasPermission(P.CampaignRecordUpdate) || isAdmin,
      canCreateConcepts: hasPermission(P.CampaignConceptCreate) || isAdmin,
      canUpdateConcepts: hasPermission(P.CampaignConceptUpdate) || isAdmin,
      canDeleteConcepts: hasPermission(P.CampaignConceptDelete) || isAdmin,
      canDeleteCollections: hasPermission(P.DeleteMobileCollections) || isAdmin,
      canAddCollectionMemberships: hasPermission(P.CreateCollectionMembership) || isAdmin,
      canRemoveCollectionMemberships: hasPermission(P.DeleteCollectionMembership) || isAdmin,
      canManageSettings: isAdmin,

      canGeneratePDFs: Object.values(P).filter(p => p.startsWith("product:")).some(hasPermission) ||
                      hasRole('ProductManager') ||
                      isAdmin,

      isAdmin,
      canAccessDemandManagement,
      canAccessDemandDashboard,
      canAccessCampaignManagement,
      canAddCampaigns,
      canDeleteCampaigns,
      canManageHubSpot,
      canAccessConceptManagement,
      canManageConcepts,
      canReadCollections: hasPermission(P.ReadMobileCollections) || isAdmin,
      canCreateCollections: hasPermission(P.CreateMobileCollections) || isAdmin,
      canUpdateCollections: hasPermission(P.UpdateMobileCollections) || isAdmin,
      canWriteCollectionMemberships: hasPermission(P.CreateCollectionMembership) || isAdmin,
      canReadProductSearch,
      canManageProductSearchSync,
    };

    return result;
  }, [permissions, roles, isAuthenticated, user, loading, userId]);

  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated || !user || !userId) {
        setLoading(false);
      return;
    }


    // Check if we have valid cached permissions for this user
    const now = Date.now();
    if (permissionsCache &&
        permissionsCache.userId === userId &&
        (now - permissionsCache.timestamp) < CACHE_DURATION) {
        setPermissions(permissionsCache.permissions);
      setRoles(permissionsCache.roles);
      setLoading(false);
      return;
    }

    try {

      const userPermissions = collectStringClaims(user, [
        'https://demandmanager.com/permissions',
        `${AUTH0_AUDIENCE}/permissions`,
        'permissions',
      ]);
      const userRoles = collectStringClaims(user, [
        'https://demandmanager.com/roles',
        `${AUTH0_AUDIENCE}/roles`,
        'roles',
      ]);


      // API permissions belong to the access token. Let the SDK reuse a valid token or renew an expired one.
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH0_AUDIENCE,
          scope: authScopes,
        },
      });


      // Decode the access token to extract permissions and roles
      const tokenPayload = decodeJWT(token);


      // Extract permissions from token (Auth0 includes them in 'permissions' claim)
      const tokenPermissions = collectStringClaims(tokenPayload, [
        'permissions',
        'https://demandmanager.com/permissions',
        `${AUTH0_AUDIENCE}/permissions`,
      ]);

      const tokenRoles = collectStringClaims(tokenPayload, [
        'roles',
        'https://demandmanager.com/roles',
        `${AUTH0_AUDIENCE}/roles`,
      ]);

      // Also check scope for permissions (as backup)
      const scopePermissions = typeof tokenPayload.scope === 'string' ? tokenPayload.scope.split(' ') : [];


      // Combine all permission sources
      const allPermissions = [...new Set([...userPermissions, ...tokenPermissions, ...scopePermissions])];
      const allRoles = [...new Set([...userRoles, ...tokenRoles])];


      setPermissions(allPermissions);
      setRoles(allRoles);

      // Cache the results
      permissionsCache = {
        permissions: allPermissions,
        roles: allRoles,
        userId,
        timestamp: now
      };


    } catch (error) {
      console.error('? [fetchPermissions] Error fetching Auth0 permissions:', error);

      // Background permission checks must never navigate away from the current page.
      // Keep protected features unavailable until a token can be obtained.
      permissionsCache = null;
      // Fallback to empty permissions
      setRoles([]);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [user, isAuthenticated, getAccessTokenSilently, userId]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  return calculatedPermissions;
}

export function useAuthenticatedUser() {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();

  const getToken = useCallback(async (): Promise<string> => {
    if (!isAuthenticated) {
      throw new Error('User is not authenticated');
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH0_AUDIENCE,
          scope: authScopes,
        },
      });

      if (!token) {
        throw new Error('Failed to obtain access token');
      }

      return token;
    } catch (error) {
      console.error('Error getting access token:', error);

      // API callers handle token failures; only explicit sign-in starts a redirect.
      throw error;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  return {
    user,
    isAuthenticated,
    getToken,
  };
}

function collectStringClaims(source: Record<string, unknown> | undefined, claimNames: string[]): string[] {
  return [...new Set(claimNames.flatMap((claimName) => {
    const value = source?.[claimName];
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
  }))];
}
